import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import * as Icons from "lucide-react";
import { ArrowLeft, Loader2, Play, Gift } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { CATEGORIES, DIFFICULTIES, TIMER_OPTIONS, FALLBACK_QUESTIONS } from "@/data/mockData";
import { fetchVenue, prizeEmoji } from "@/lib/api";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_BG = {
  amber: "bg-amber-400 text-slate-950",
  pink: "bg-pink-500 text-white",
  purple: "bg-purple-500 text-white",
  cyan: "bg-cyan-400 text-slate-950",
  green: "bg-green-500 text-white",
};

export default function QuizSetup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const venue = params.get("venue") || "Solo Play";
  const presetCat = params.get("cat");
  const venueId = params.get("venueId");
  const { setSession } = useApp();

  const [category, setCategory] = useState(presetCat || "general");
  const [difficulty, setDifficulty] = useState("medium");
  const [timer, setTimer] = useState(15);
  const [loading, setLoading] = useState(false);
  const [prize, setPrize] = useState(null);

  useEffect(() => {
    if (venueId) {
      fetchVenue(venueId)
        .then((v) => { if (v.prize?.title) setPrize(v.prize); })
        .catch(() => {});
    }
  }, [venueId]);

  const start = async () => {
    const cat = CATEGORIES.find((c) => c.id === category);
    setLoading(true);
    let questions;
    try {
      const res = await axios.post(`${API}/generate-quiz`, { category: cat.name, difficulty }, { timeout: 45000 });
      questions = res.data.questions;
      if (!questions || questions.length < 5) throw new Error("short");
    } catch (e) {
      toast.message("Using our backup question set 🍺", { description: "The quiz master had a hiccup." });
      questions = FALLBACK_QUESTIONS;
    }
    setSession({
      questions,
      category: cat.name,
      categoryId: cat.id,
      difficulty,
      timer,
      venue,
      venueId: venueId || null,
      prize: prize || null,
    });
    setLoading(false);
    navigate("/quiz");
  };

  return (
    <div className="p-6">
      <div className="flex items-center gap-3 mb-2 pt-4">
        <button data-testid="setup-back-btn" onClick={() => navigate("/")} className="text-slate-400 p-1">
          <ArrowLeft strokeWidth={2.5} />
        </button>
        <h1 className="font-display text-3xl font-black text-slate-50">Set Up Quiz</h1>
      </div>
      <p className="text-slate-400 mb-6">{venue}</p>

      {prize?.title && (
        <div data-testid="setup-prize-banner" className="bg-slate-900 border border-amber-400/30 rounded-2xl p-4 mb-6 flex items-center gap-3">
          <Gift size={22} className="text-amber-400 shrink-0" />
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-amber-400">Prize up for grabs</p>
            <p className="font-bold text-slate-100 text-sm">{prizeEmoji(prize.kind)} {prize.title}</p>
          </div>
        </div>
      )}

      <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-3 block">Category</label>
      <div className="grid grid-cols-2 gap-3 mb-8" data-testid="category-grid">
        {CATEGORIES.map((c) => {
          const Icon = Icons[c.icon] || Icons.Brain;
          const active = category === c.id;
          return (
            <motion.button
              key={c.id}
              data-testid={`category-${c.id}`}
              whileTap={{ scale: 0.95 }}
              onClick={() => setCategory(c.id)}
              className={`rounded-2xl p-4 flex flex-col items-start gap-2 border-2 no-tap-highlight ${active ? COLOR_BG[c.color] + " border-transparent" : "bg-slate-900 border-slate-800 text-slate-300"}`}
            >
              <Icon size={24} strokeWidth={2.5} />
              <span className="font-bold text-sm text-left leading-tight">{c.name}</span>
            </motion.button>
          );
        })}
      </div>

      <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-3 block">Difficulty</label>
      <div className="grid grid-cols-3 gap-3 mb-8" data-testid="difficulty-grid">
        {DIFFICULTIES.map((d) => {
          const active = difficulty === d.id;
          return (
            <motion.button
              key={d.id}
              data-testid={`difficulty-${d.id}`}
              whileTap={{ scale: 0.95 }}
              onClick={() => setDifficulty(d.id)}
              className={`rounded-2xl p-4 border-2 no-tap-highlight ${active ? "bg-amber-400 text-slate-950 border-transparent" : "bg-slate-900 border-slate-800 text-slate-300"}`}
            >
              <div className="font-display font-black">{d.name}</div>
              <div className="text-[10px] opacity-80 mt-0.5">{d.desc}</div>
            </motion.button>
          );
        })}
      </div>

      <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-3 block">Seconds per question</label>
      <div className="grid grid-cols-3 gap-3 mb-10" data-testid="timer-grid">
        {TIMER_OPTIONS.map((t) => (
          <motion.button
            key={t}
            data-testid={`timer-${t}`}
            whileTap={{ scale: 0.95 }}
            onClick={() => setTimer(t)}
            className={`rounded-2xl py-4 border-2 font-display font-black text-xl no-tap-highlight ${timer === t ? "bg-cyan-400 text-slate-950 border-transparent" : "bg-slate-900 border-slate-800 text-slate-300"}`}
          >
            {t}s
          </motion.button>
        ))}
      </div>

      <motion.button
        data-testid="setup-start-btn"
        whileTap={{ scale: 0.97 }}
        disabled={loading}
        onClick={start}
        className="w-full h-16 bg-pink-500 text-white rounded-2xl font-display font-black text-xl flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(236,72,153,0.4)] disabled:opacity-70 no-tap-highlight"
      >
        {loading ? (<><Loader2 className="animate-spin" /> Brewing questions…</>) : (<><Play strokeWidth={3} fill="currentColor" /> Generate & Play</>)}
      </motion.button>
    </div>
  );
}
